import { useState, useEffect, useRef, useCallback } from 'react';

const NOTIF_KEY = 'prayer_notifications';
const PRAYERS_EN = { Fajr: 'Fajr', Dhuhr: 'Dhuhr', Asr: 'Asr', Maghrib: 'Maghrib', Isha: 'Isha' };

/**
 * usePrayerNotifications — request browser notification permission
 * and schedule in-tab notifications for each upcoming prayer time.
 * Works while the browser tab is open.
 */
export const usePrayerNotifications = (prayerTimes) => {
  const [permission, setPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return localStorage.getItem(NOTIF_KEY) || Notification.permission;
  });

  const timeoutsRef = useRef([]);

  const isSupported = typeof Notification !== 'undefined';
  const isGranted = permission === 'granted';

  const requestPermission = useCallback(async () => {
    if (!isSupported) return 'unsupported';
    const perm = await Notification.requestPermission();
    setPermission(perm);
    localStorage.setItem(NOTIF_KEY, perm);
    return perm;
  }, [isSupported]);

  // Schedule browser notifications for today's remaining prayers
  useEffect(() => {
    // Clear any existing timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    if (!isGranted || !prayerTimes) return;

    const now = new Date();
    const today = now.toLocaleDateString('en-CA'); // YYYY-MM-DD

    Object.entries(PRAYERS_EN).forEach(([key, label]) => {
      const timeStr = prayerTimes[key];
      if (!timeStr) return;

      const [hours, minutes] = timeStr.split(':').map(Number);
      const prayerDate = new Date();
      prayerDate.setHours(hours, minutes, 0, 0);

      const msUntil = prayerDate - now;
      if (msUntil <= 0 || msUntil > 24 * 60 * 60 * 1000) return; // Only today's upcoming

      const t = setTimeout(() => {
        if (Notification.permission !== 'granted') return;
        try {
          const n = new Notification(`🕌 ${label} Prayer Time`, {
            body: `It's time for ${label} (${timeStr})`,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: `prayer-${key}-${today}`,
            renotify: false,
            silent: false,
          });
          // Auto-close after 15 seconds
          setTimeout(() => n.close(), 15000);
        } catch (e) {
          console.warn('Notification failed:', e);
        }
      }, msUntil);

      timeoutsRef.current.push(t);
    });

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [isGranted, prayerTimes]);

  return { permission, isSupported, isGranted, requestPermission };
};
